// Localization Context for React Admin Panel
import React, { createContext, useContext, useEffect, useState } from 'react';
import { localizationService, LocalizationContext as LocalizationContextType } from '@shared/services/localization';
import { LanguageCode } from '@shared/constants/languages';

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

export function LocalizationProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(localizationService.getLanguage());

  useEffect(() => {
    // Subscribe to language changes
    const unsubscribe = localizationService.addLanguageChangeListener((newLanguage) => {
      setLanguageState(newLanguage);
    });

    return unsubscribe;
  }, []);

  const setLanguage = (newLanguage: LanguageCode) => {
    localizationService.setLanguage(newLanguage);
  };

  const t = (key: string, values?: Record<string, any>) => {
    return localizationService.t(key, values);
  };

  const formatCurrency = (amount: number, currency?: string) => {
    return localizationService.formatCurrency(amount, currency);
  };

  const formatDate = (date: Date, format?: string) => {
    return localizationService.formatDate(date, format);
  };

  const formatNumber = (number: number, options?: Intl.NumberFormatOptions) => {
    return localizationService.formatNumber(number, options);
  };

  const isRTL = localizationService.isRTL();

  const value: LocalizationContextType = {
    language,
    setLanguage,
    t,
    formatCurrency,
    formatDate,
    formatNumber,
    isRTL,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
}

export function useLocalization() {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
}

// Higher-order component for components that need localization
export function withLocalization<P extends object>(
  Component: React.ComponentType<P & LocalizationContextType>
) {
  return function LocalizedComponent(props: P) {
    const localization = useLocalization();
    return <Component {...props} {...localization} />;
  };
}

// Hook for getting supported languages
export function useSupportedLanguages() {
  return localizationService.getSupportedLanguages();
}

// Hook for language switching with confirmation
export function useLanguageSwitcher() {
  const { language, setLanguage } = useLocalization();
  const supportedLanguages = useSupportedLanguages();

  const switchLanguage = (newLanguage: LanguageCode) => {
    if (newLanguage !== language) {
      // In a real app, you might want to show a confirmation dialog
      // or save user preferences before switching
      setLanguage(newLanguage);
    }
  };

  return {
    currentLanguage: language,
    supportedLanguages,
    switchLanguage,
  };
} 